import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DownloadIcon from "@mui/icons-material/Download";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import Badge from "@mui/material/Badge";
import {
  deleteNotebook,
  downloadAllNotebooksAsZip,
  downloadNotebookAsZip,
  generateShareLink,
  getCourseRuns,
  getCourses,
  getLectures,
  getNotebooks,
  getRagEligibleNotebooks,
} from "../services/api";
import { Course, CourseRun, Lecture, Notebook } from "../types";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import RagButton from "../components/RagButton";

export const NotebooksListPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const courseIdParam = queryParams.get("courseId");
  const courseRunIdParam = queryParams.get("courseRunId");
  const lectureIdParam = queryParams.get("lectureId");

  const [selectedCourseId, setSelectedCourseId] = useState<number | "">(
    courseIdParam ? Number(courseIdParam) : ""
  );
  const [selectedCourseRunId, setSelectedCourseRunId] = useState<number | "">(
    courseRunIdParam ? Number(courseRunIdParam) : ""
  );
  const [selectedLectureId, setSelectedLectureId] = useState<number | "">(
    lectureIdParam ? Number(lectureIdParam) : ""
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [courses, setCourses] = useState<Course[]>([]);
  const [courseRuns, setCourseRuns] = useState<CourseRun[]>([]);
  const [lectures, setLectures] = useState<Lecture[]>([]);
  const [notebooks, setNotebooks] = useState<Notebook[]>([]);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [generatingLink, setGeneratingLink] = useState<number | null>(null);
  const [downloadingNotebook, setDownloadingNotebook] = useState<number | null>(
    null
  );
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [deletingNotebook, setDeletingNotebook] = useState<number | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [notebookToDelete, setNotebookToDelete] = useState<{
    id: number;
    title: string;
  } | null>(null);

  // Новые состояния для RAG
  const [ragEligibleNotebooks, setRagEligibleNotebooks] = useState<number[]>(
    []
  );

  // Функция для обновления URL с параметрами
  const updateUrlParams = (params: {
    courseId?: number | "";
    courseRunId?: number | "";
    lectureId?: number | "";
  }) => {
    const newParams = new URLSearchParams(location.search);

    if (params.courseId !== undefined) {
      if (params.courseId === "") {
        newParams.delete("courseId");
      } else {
        newParams.set("courseId", params.courseId.toString());
      }
    }

    if (params.courseRunId !== undefined) {
      if (params.courseRunId === "") {
        newParams.delete("courseRunId");
      } else {
        newParams.set("courseRunId", params.courseRunId.toString());
      }
    }

    if (params.lectureId !== undefined) {
      if (params.lectureId === "") {
        newParams.delete("lectureId");
      } else {
        newParams.set("lectureId", params.lectureId.toString());
      }
    }

    navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
  };

  // Функции для загрузки данных
  const fetchCourses = async () => {
    try {
      const coursesData = await getCourses();
      setCourses(coursesData);
      return coursesData;
    } catch (err) {
      console.error("Ошибка при загрузке курсов:", err);
      setError("Не удалось загрузить список курсов");
      return [];
    }
  };

  const fetchCourseRuns = async (courseId: number) => {
    try {
      const courseRunsData = await getCourseRuns(courseId);
      setCourseRuns(courseRunsData);
      return courseRunsData;
    } catch (err) {
      console.error("Ошибка при загрузке потоков курса:", err);
      setError("Не удалось загрузить список потоков курса");
      return [];
    }
  };

  const fetchLectures = async (courseRunId: number) => {
    try {
      const lecturesData = await getLectures(courseRunId);
      setLectures(lecturesData);
      return lecturesData;
    } catch (err) {
      console.error("Ошибка при загрузке лекций:", err);
      setError("Не удалось загрузить список лекций");
      return [];
    }
  };

  const fetchNotebooks = async (lectureId: number) => {
    setLoading(true);
    setError(null);

    try {
      const notebooksData = await getNotebooks(lectureId);
      setNotebooks(notebooksData);
      return notebooksData;
    } catch (err) {
      console.error("Ошибка при загрузке ноутбуков:", err);
      setError("Не удалось загрузить список ноутбуков");
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      const coursesData = await fetchCourses();

      if (selectedCourseId !== "") {
        const courseExists = coursesData.some(
          (course) => course.id === selectedCourseId
        );

        if (courseExists) {
          const courseRunsData = await fetchCourseRuns(
            selectedCourseId as number
          );

          if (selectedCourseRunId !== "") {
            const courseRunExists = courseRunsData.some(
              (run) => run.id === selectedCourseRunId
            );

            if (courseRunExists) {
              const lecturesData = await fetchLectures(
                selectedCourseRunId as number
              );

              if (selectedLectureId !== "") {
                const lectureExists = lecturesData.some(
                  (lecture) => lecture.id === selectedLectureId
                );

                if (lectureExists) {
                  await fetchNotebooks(selectedLectureId as number);
                }
              }
            } else {
              setSelectedCourseRunId("");
              setSelectedLectureId("");
              updateUrlParams({ courseRunId: "", lectureId: "" });
            }
          }
        } else {
          setSelectedCourseId("");
          setSelectedCourseRunId("");
          setSelectedLectureId("");
          updateUrlParams({ courseId: "", courseRunId: "", lectureId: "" });
        }
      }
    };

    initializeData();
  }, [location.search]);

  const handleCourseChange = async (courseId: number | "") => {
    setSelectedCourseId(courseId);
    setSelectedCourseRunId("");
    setSelectedLectureId("");
    setCourseRuns([]);
    setLectures([]);
    setNotebooks([]);

    updateUrlParams({ courseId, courseRunId: "", lectureId: "" });

    if (courseId !== "") {
      await fetchCourseRuns(courseId as number);
    }
  };

  const handleCourseRunChange = async (courseRunId: number | "") => {
    setSelectedCourseRunId(courseRunId);
    setSelectedLectureId("");
    setLectures([]);
    setNotebooks([]);

    // Обновляем URL
    updateUrlParams({ courseRunId, lectureId: "" });

    // Загружаем лекции, если выбран поток курса
    if (courseRunId !== "") {
      await fetchLectures(courseRunId as number);
    }
  };

  const handleLectureChange = async (lectureId: number | "") => {
    setSelectedLectureId(lectureId);
    setNotebooks([]);

    // Обновляем URL
    updateUrlParams({ lectureId });

    // Загружаем ноутбуки, если выбрана лекция
    if (lectureId !== "") {
      await fetchNotebooks(lectureId as number);
    }
  };

  // Функция для генерации и копирования ссылки для студентов
  const handleGenerateShareLink = async (
    notebookId: number,
    isPublic: boolean = false,
    openInNewWindow: boolean = false
  ) => {
    setGeneratingLink(notebookId);
    try {
      const response = await generateShareLink(notebookId);
      const token = response.token;

      if (isPublic) {
        if (openInNewWindow) {
          // Открываем в новом окне
          window.open(`/share/${token}`, "_blank");
        } else {
          // Перенаправляем на страницу публичного просмотра
          navigate(`/share/${token}`);
        }
        return;
      }

      // Копируем ссылку в буфер обмена
      const shareUrl = `${window.location.origin}/review/${token}`;
      await navigator.clipboard.writeText(shareUrl);
      setToastMessage("Ссылка скопирована в буфер обмена");
    } catch (err) {
      console.error("Ошибка при генерации ссылки:", err);
      setError("Не удалось сгенерировать ссылку");
    } finally {
      setGeneratingLink(null);
    }
  };

  // Функция для скачивания ноутбука в виде zip-архива
  const handleDownloadNotebook = async (notebookId: number, title: string) => {
    setDownloadingNotebook(notebookId);
    try {
      const blob = await downloadNotebookAsZip(notebookId);

      // Создаем ссылку для скачивания
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${title.replace(
        /[^a-zA-Zа-яА-Я0-9]/g,
        "_"
      )}_${notebookId}.zip`;
      document.body.appendChild(a);
      a.click();

      // Очищаем ресурсы
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setToastMessage(`Ноутбук "${title}" скачан!`);
    } catch (err) {
      console.error("Ошибка при скачивании ноутбука:", err);
      setError("Не удалось скачать ноутбук");
    } finally {
      setDownloadingNotebook(null);
    }
  };

  // Функция для скачивания всех ноутбуков лекции в виде zip-архива
  const handleDownloadAllNotebooks = async () => {
    if (!selectedLectureId) return;

    setDownloadingAll(true);
    try {
      const blob = await downloadAllNotebooksAsZip(selectedLectureId as number);

      // Находим название лекции
      const lecture = lectures.find((l) => l.id === selectedLectureId);
      const lectureTitle = lecture
        ? lecture.title
        : `lecture_${selectedLectureId}`;

      // Создаем ссылку для скачивания
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${lectureTitle.replace(
        /[^a-zA-Zа-яА-Я0-9]/g,
        "_"
      )}_notebooks.zip`;
      document.body.appendChild(a);
      a.click();

      // Очищаем ресурсы
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setToastMessage("Все ноутбуки скачаны!");
    } catch (err) {
      console.error("Ошибка при скачивании всех ноутбуков:", err);
      setError("Не удалось скачать все ноутбуки");
    } finally {
      setDownloadingAll(false);
    }
  };

  const handleDeleteNotebook = async (notebookId: number, title: string) => {
    setNotebookToDelete({ id: notebookId, title });
    setConfirmDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!notebookToDelete) return;

    setDeletingNotebook(notebookToDelete.id);
    setConfirmDeleteOpen(false);

    try {
      await deleteNotebook(notebookToDelete.id);

      // Обновляем список ноутбуков после удаления
      setNotebooks(
        notebooks.filter((notebook) => notebook.id !== notebookToDelete.id)
      );

      // Показываем сообщение об успешном удалении
      setToastMessage("Ноутбук успешно удален");
    } catch (err) {
      console.error("Ошибка при удалении ноутбука:", err);
      setError("Не удалось удалить ноутбук");
    } finally {
      setDeletingNotebook(null);
      setNotebookToDelete(null);
    }
  };

  const cancelDelete = () => {
    setConfirmDeleteOpen(false);
    setNotebookToDelete(null);
  };

  const fetchRagEligibleNotebooks = async () => {
    try {
      const eligibleNotebooks = await getRagEligibleNotebooks(
        selectedLectureId
      );
      setRagEligibleNotebooks(eligibleNotebooks);
    } catch (err) {
      console.error("Ошибка при загрузке списка ноутбуков для RAG:", err);
    }
  };

  useEffect(() => {
    fetchRagEligibleNotebooks();
  }, []);

  useEffect(() => {
    if (notebooks.length > 0) {
      fetchRagEligibleNotebooks();
    }
  }, [notebooks]);

  return (
    <Box sx={{ p: 4 }}>
      <Paper sx={{ p: 3, maxWidth: 800, mx: "auto" }}>
        <Typography variant="h5" component="h1" gutterBottom>
          Список ноутбуков
        </Typography>

        <Stack spacing={3} sx={{ mt: 4 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <FormControl fullWidth>
            <InputLabel id="course-select-label">Курс</InputLabel>
            <Select
              labelId="course-select-label"
              id="course-select"
              value={selectedCourseId}
              label="Курс"
              onChange={(e) => handleCourseChange(e.target.value as number)}
              disabled={loading || courses.length === 0}
            >
              {courses.map((course) => (
                <MenuItem key={course.id} value={course.id}>
                  {course.title}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>Выберите курс</FormHelperText>
          </FormControl>

          <FormControl fullWidth disabled={selectedCourseId === "" || loading}>
            <InputLabel id="course-run-select-label">Поток курса</InputLabel>
            <Select
              labelId="course-run-select-label"
              id="course-run-select"
              value={selectedCourseRunId}
              label="Поток курса"
              onChange={(e) => handleCourseRunChange(e.target.value as number)}
              disabled={
                selectedCourseId === "" || loading || courseRuns.length === 0
              }
            >
              {courseRuns.map((courseRun) => (
                <MenuItem key={courseRun.id} value={courseRun.id}>
                  {courseRun.title}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>Выберите поток курса</FormHelperText>
          </FormControl>

          <FormControl
            fullWidth
            disabled={selectedCourseRunId === "" || loading}
          >
            <InputLabel id="lecture-select-label">Лекция</InputLabel>
            <Select
              labelId="lecture-select-label"
              id="lecture-select"
              value={selectedLectureId}
              label="Лекция"
              onChange={(e) => handleLectureChange(e.target.value as number)}
              disabled={
                selectedCourseRunId === "" || loading || lectures.length === 0
              }
            >
              {lectures.map((lecture) => (
                <MenuItem key={lecture.id} value={lecture.id}>
                  {lecture.title}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>Выберите лекцию</FormHelperText>
          </FormControl>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            selectedLectureId !== "" && (
              <Box sx={{ mt: 4 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6">Ноутбуки</Typography>
                  {notebooks.length > 0 && (
                    <Button
                      variant="outlined"
                      startIcon={
                        downloadingAll ? (
                          <CircularProgress size={20} />
                        ) : (
                          <FileDownloadIcon />
                        )
                      }
                      onClick={handleDownloadAllNotebooks}
                      disabled={downloadingAll}
                    >
                      Скачать все
                    </Button>
                  )}
                </Box>
                <Table size="small" sx={{ mb: 3, maxWidth: 300 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Работ</TableCell>
                      <TableCell>Комментариев</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      {/* общее число ноутбуков */}
                      <TableCell>{notebooks.length}</TableCell>
                      {/* сумма всех notebook.nComments */}
                      <TableCell>
                        {notebooks.reduce(
                          (sum, nb) => sum + (nb.nComments ?? 0),
                          0
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                {notebooks.length > 0 ? (
                  <List>
                    {notebooks.map((notebook) => (
                      <React.Fragment key={notebook.id}>
                        <ListItem
                          secondaryAction={
                            <Box display="flex" alignItems="center">
                              <Tooltip title="Количество комментариев">
                                <Badge
                                  badgeContent={notebook.nComments}
                                  color="primary"
                                  sx={{ mr: 3 }}
                                ></Badge>
                              </Tooltip>

                              <RagButton
                                notebook={notebook}
                                isEligibleForRag={ragEligibleNotebooks.includes(
                                  notebook.id
                                )}
                                onChangeRagStatus={(status) =>
                                  setNotebooks((p) =>
                                    p.map((nb) => {
                                      if (nb.id === notebook.id) {
                                        return { ...nb, ragStatus: status };
                                      }
                                      return nb;
                                    })
                                  )
                                }
                                onError={setError}
                                displayRagSuccessMessage={setToastMessage}
                                removeEligibilityForRag={() =>
                                  setRagEligibleNotebooks((p) =>
                                    p.filter((id) => id !== notebook.id)
                                  )
                                }
                              />

                              <Tooltip title="Скачать ноутбук">
                                <IconButton
                                  edge="end"
                                  aria-label="download"
                                  onClick={() =>
                                    handleDownloadNotebook(
                                      notebook.id,
                                      notebook.title
                                    )
                                  }
                                  disabled={downloadingNotebook === notebook.id}
                                  sx={{ mr: 1 }}
                                >
                                  {downloadingNotebook === notebook.id ? (
                                    <CircularProgress size={20} />
                                  ) : (
                                    <DownloadIcon />
                                  )}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Ноутбук с комментариями для студента">
                                <IconButton
                                  onClick={() =>
                                    handleGenerateShareLink(
                                      notebook.id,
                                      true,
                                      true
                                    )
                                  }
                                  disabled={generatingLink === notebook.id}
                                  edge="end"
                                  aria-label="public-view"
                                  sx={{ mr: 1 }}
                                >
                                  {generatingLink === notebook.id ? (
                                    <CircularProgress size={20} />
                                  ) : (
                                    <VisibilityIcon />
                                  )}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Редактировать ноутбук">
                                <IconButton
                                  component={Link}
                                  to={`/notebooks/${notebook.id}`}
                                  edge="end"
                                  aria-label="edit"
                                  sx={{ mr: 1 }}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Удалить ноутбук">
                                <IconButton
                                  edge="end"
                                  aria-label="delete"
                                  onClick={() =>
                                    handleDeleteNotebook(
                                      notebook.id,
                                      notebook.title
                                    )
                                  }
                                  disabled={deletingNotebook === notebook.id}
                                  color="error"
                                >
                                  {deletingNotebook === notebook.id ? (
                                    <CircularProgress size={20} />
                                  ) : (
                                    <DeleteIcon />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </Box>
                          }
                        >
                          <ListItemText
                            primary={notebook.title}
                            secondary={`Создан: ${new Date(
                              notebook.createdAt
                            ).toLocaleString()}`}
                          />
                        </ListItem>
                        <Divider component="li" />
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Alert severity="info">
                    Ноутбуки для выбранной лекции не найдены
                  </Alert>
                )}
              </Box>
            )
          )}
        </Stack>
      </Paper>

      <Snackbar
        open={!!toastMessage}
        autoHideDuration={3000}
        onClose={() => setToastMessage(null)}
        message={toastMessage}
      />

      {/* Диалог подтверждения удаления */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={cancelDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Подтверждение удаления
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Вы действительно хотите удалить ноутбук "{notebookToDelete?.title}"?
            Это действие нельзя будет отменить.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete}>Отмена</Button>
          <Button onClick={confirmDelete} color="error" autoFocus>
            Удалить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
